import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';
import CategoryPage from './CategoryPage';

function PizzaCategory() {
  const { city_name, vehicle_type, site_title } = useContext(ConfigContext);
  const vehicleLowerCase = vehicle_type.toLowerCase();
  const { content: cms } = usePageContent('cuisine_pizza');

  const MainText = (
    <>
      <h1>{cms?.main?.heading || `Pizza food ${vehicleLowerCase}s in ${city_name}`}</h1>
      <CmsRichText
        html={cms?.main?.body}
        fallback={
          <p>Looking for wood-fired Neapolitan, New York slices, Detroit squares, or a gluten-free crust? Browse { city_name } pizza { vehicleLowerCase }s, check menus and schedules, or request catering for an event.</p>
        }
      />
    </>
  );

  const SecondaryText = (
    <>
      <h3 className='u-phosphate'>{cms?.secondary?.heading || 'What you’ll find'}</h3>
      <CmsRichText
        html={cms?.secondary?.body}
        fallback={
          <p>Pizza { vehicleLowerCase }s usually commit to one style: wood-fired Neapolitan pies baked in a couple of minutes, New York-style by the slice, or thicker Detroit and Sicilian squares. Menus run short, with a margherita, a pepperoni, a white pie, and a special or two. Ask about gluten-free crust and vegan cheese before you get in line.</p>
        }
      />

      { city_name === 'Seattle' && !cms &&
        <>
          <h3 className='u-phosphate u-mt4'>Seattle’s Must-Try Pizza Trucks</h3>
          <p>Seattle is home to a diverse selection of pizza trucks, each offering its own spin on the classic pie. <Link to='/food-trucks/candela-pizza'>Candela Pizza</Link> is a must-try for those looking for a unique fusion experience. Blending Latin American flavors with traditional pizza-making techniques, they serve up creations like the Havana Pizza, featuring Cuban roast pork, banana peppers, pickles, and a smoky chipotle aioli. Another standout is <Link to='/food-trucks/delfino-s-chicago-style-pizza'>Delfino’s Chicago Style Pizza</Link>, a truck that brings the deep-dish experience straight from the Windy City to the Pacific Northwest. Their buttery, flaky crust is loaded with rich tomato sauce, gooey cheese, and toppings that make each slice a meal in itself.</p>
          <p>For Neapolitan pizza lovers, <Link to='/food-trucks/d-asporto'>D’Asporto</Link> is a game-changer. This truck is part of the Tutta Bella family and brings the flavors of Italy on the go, using high-quality ingredients and an authentic wood-fired oven imported from Italy. If you’re craving classic New York-style slices, <Link to='/food-trucks/oskar-s-pizza'>Oskar’s Pizza</Link> has you covered with its hand-tossed 18-inch pies, available whole or by the slice. Their menu includes everything from simple cheese and pepperoni to loaded options like the McQueen, topped with salami, mushrooms, onions, and spicy peppers.</p>
          <p>Meanwhile, <Link to='/food-trucks/pompeii-wood-fired-pizza'>Pompeii Wood Fired Pizza</Link> and <Link to='/food-trucks/stella-fiore-wood-fired-pizza'>Stella Fiore</Link> are perfect for those who appreciate the artistry of a traditional wood-fired crust. Their pizzas offer the perfect balance of crispy edges and chewy centers, with toppings ranging from classic margherita to more adventurous combinations like Florentine pizzas with ricotta and roasted red peppers. Whether you’re after an ultra-thin crust, a deep-dish masterpiece, or a creative fusion pizza, there’s a truck in Seattle serving up exactly what you need.</p>
        </>
      }

      <h3 className='u-phosphate u-mt4'>{cms?.events?.heading || 'Pizza catering for your event'}</h3>
      <CmsRichText
        html={cms?.events?.body}
        fallback={
          <p>Wood-fired ovens bake fast, but one oven has a ceiling. Ask how many pies per hour the { vehicleLowerCase } can serve for a group your size, and whether guests order by the slice or by the pie. Tell us your date, location, and guest count. We’ll find { vehicleLowerCase }s that fit.</p>
        }
      />
    </>
  );

  return (
    <CategoryPage
      foodName='Pizza'
      foodCategory='pizza'
      seoTitle={cms?.meta?.title || `Pizza food ${vehicleLowerCase}s in ${city_name}`}
      seoDescription={cms?.meta?.description || `Find and book pizza food ${vehicleLowerCase}s in ${city_name}. Browse menus, compare quotes, and hire pizza catering for your next event.`}
      seoCanonical='/pizza-food-trucks'
      MainText={MainText}
      SecondaryText={SecondaryText}
    />
  );
}

export default PizzaCategory;
